// components/views/Favorites.js
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import { useApp } from '../../context/AppContext';
import FavoritesService from '../../services/favorites';
import '../../styles/favorites.css';

const Favorites = () => {
    const { language } = useLanguage();
    const { t } = useTranslation(language);
    const { refreshFavorites } = useApp();
    const [favorites, setFavorites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedGenre, setSelectedGenre] = useState(null);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(null); // artistId da confermare
    const [sortBy, setSortBy] = useState('date'); // 'date' o 'name'
    const [openMenuId, setOpenMenuId] = useState(null);

    // Carica preferiti (solo una volta, ordinamento client-side)
    useEffect(() => {
        const loadFavorites = async () => {
            try {
                setLoading(true);
                const data = await FavoritesService.getUserFavorites();
                setFavorites(data);
            } catch (error) {
                console.error('[Favorites] Error loading favorites:', error);
            } finally {
                setLoading(false);
            }
        };

        loadFavorites();
    }, []);

    // Estrai generi unici da tutti i preferiti
    const allGenres = React.useMemo(() => {
        const genreSet = new Set();
        favorites.forEach(artist => {
            if (artist.genres && Array.isArray(artist.genres)) {
                artist.genres.forEach(genre => genreSet.add(genre));
            }
        });
        return Array.from(genreSet).sort();
    }, [favorites]);

    // Filtra e ordina artisti (client-side per evitare "scatti")
    const filteredFavorites = React.useMemo(() => {
        let filtered = favorites;
        
        // Filtra per genere
        if (selectedGenre) {
            filtered = filtered.filter(artist => 
                artist.genres && artist.genres.includes(selectedGenre)
            );
        }
        
        // Ordina client-side
        const sorted = [...filtered].sort((a, b) => {
            if (sortBy === 'name') {
                return a.name.localeCompare(b.name, language === 'it' ? 'it' : 'en');
            } else {
                // Ordina per data (più recente prima)
                const dateA = new Date(a.saved_at);
                const dateB = new Date(b.saved_at);
                return dateB - dateA;
            }
        });
        
        return sorted;
    }, [favorites, selectedGenre, sortBy, language]);

    useEffect(() => {
        const handleClickOutside = () => setOpenMenuId(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Rimuovi dai preferiti
    const handleRemoveFavorite = async (artistId) => {
        try {
            await FavoritesService.removeFavorite(artistId);
            // Aggiorna lista locale
            setFavorites(prev => prev.filter(a => a.id !== artistId));
            // Aggiorna context
            await refreshFavorites();
            setShowRemoveConfirm(null);
        } catch (error) {
            console.error('[Favorites] Error removing favorite:', error);
        }
    };

    // Condividi artista
    const handleShare = async (artist) => {
        const artistName = artist.name;
        const spotifyUrl = artist.spotify_url || `https://open.spotify.com/search/${encodeURIComponent(artistName)}`;
        
        const shareText = language === 'it' 
            ? `Ho scoperto nuova musica con foundly.it\n\nAscolta ${artistName} su Spotify.`
            : `I discovered new music with foundly.it\n\nListen to ${artistName} on Spotify.`;
        
        if (navigator.share) {
            try {
                await navigator.share({
                    title: language === 'it' ? `Scopri ${artistName} con Foundly` : `Discover ${artistName} with Foundly`,
                    text: shareText,
                    url: spotifyUrl
                });
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Error sharing:', err);
                }
            }
        } else {
            window.open(spotifyUrl, '_blank');
        }
        setOpenMenuId(null);
    };

    // Apri Spotify
    const handleOpenSpotify = (artist) => {
        const spotifyUrl = artist.spotify_url || `https://open.spotify.com/search/${encodeURIComponent(artist.name)}`;
        window.open(spotifyUrl, '_blank');
        setOpenMenuId(null);
    };

    const toggleMenu = (event, artistId) => {
        event.stopPropagation();
        setOpenMenuId(prev => (prev === artistId ? null : artistId));
    };

    const handleMenuRemove = (artistId) => {
        setShowRemoveConfirm(artistId);
        setOpenMenuId(null);
    };
    
    // Formatta data
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const options = { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        };
        return date.toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', options);
    };

    if (loading) {
        return null; // Non mostrare nulla durante il caricamento
    }

    if (favorites.length === 0) {
        return (
            <div className="favorites-container">
                <div className="favorites-empty">
                    <span className="material-icons">star_border</span>
                    <h2>{language === 'it' ? 'Nessun preferito' : 'No favorites'}</h2>
                    <p>{language === 'it' 
                        ? 'Inizia a salvare i tuoi artisti preferiti cliccando sulla stellina'
                        : 'Start saving your favorite artists by clicking the star'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="favorites-container">
            <div className="favorites-content">
                <div className="favorites-header-image">
                    <img src="/img/love.jpg" alt="Foundly" />
                </div>

                {/* Filtri generi e ordinamento */}
                <div className="favorites-controls">
                    {allGenres.length > 0 && (
                        <div className="favorites-filters">
                            <button
                                className={`genre-filter ${selectedGenre === null ? 'active' : ''}`}
                                onClick={() => setSelectedGenre(null)}
                            >
                                {language === 'it' ? 'Tutti' : 'All'}
                            </button>
                            {allGenres.map(genre => (
                                <button
                                    key={genre}
                                    className={`genre-filter ${selectedGenre === genre ? 'active' : ''}`}
                                    onClick={() => setSelectedGenre(genre)}
                                >
                                    {genre}
                                </button>
                            ))}
                        </div>
                    )}
                    
                    {/* Ordinamento */}
                    <div className="favorites-sort">
                        <button
                            className={`sort-button ${sortBy === 'date' ? 'active' : ''}`}
                            onClick={() => setSortBy('date')}
                        >
                            {language === 'it' ? 'Data' : 'Date'}
                        </button>
                        <button
                            className={`sort-button ${sortBy === 'name' ? 'active' : ''}`}
                            onClick={() => setSortBy('name')}
                        >
                            {language === 'it' ? 'Nome' : 'Name'}
                        </button>
                    </div>
                </div>

                {/* Lista artisti */}
                <div className="favorites-list">
                    {filteredFavorites.map(artist => (
                        <div key={artist.id} className="favorite-item">
                            {/* Immagine artista */}
                            <div className="favorite-image">
                                {artist.image_url ? (
                                    <img 
                                        src={artist.image_url} 
                                        alt={artist.name}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                ) : null}
                                <div 
                                    className="favorite-image-placeholder"
                                    style={{ display: artist.image_url ? 'none' : 'flex' }}
                                >
                                    <span className="material-icons">music_note</span>
                                </div>
                            </div>

                            {/* Info artista */}
                            <div className="favorite-info">
                                <h3 className="favorite-name">{artist.name}</h3>
                                {artist.genres && artist.genres.length > 0 && (
                                    <p className="favorite-genres">
                                        {artist.genres.slice(0, 3).join(', ')}
                                    </p>
                                )}
                                <p className="favorite-date">
                                    {language === 'it' ? 'Aggiunto il ' : 'Added on '}
                                    {formatDate(artist.saved_at)}
                                </p>
                            </div>

                            {/* Azioni */}
                            <div className="favorite-actions">
                                <button
                                    className="favorite-menu-btn"
                                    onClick={(event) => toggleMenu(event, artist.id)}
                                    aria-label={language === 'it' ? 'Azioni' : 'Actions'}
                                    title={language === 'it' ? 'Azioni' : 'Actions'}
                                >
                                    <span className="material-icons">more_vert</span>
                                </button>
                                {openMenuId === artist.id && (
                                    <div className="favorite-actions-menu" onClick={(e) => e.stopPropagation()}>
                                        <button
                                            onClick={() => handleOpenSpotify(artist)}
                                        >
                                            <span className="favorite-menu-spotify-icon">
                                                <svg viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.84-.179-.84-.66 0-.419.34-.719.719-.84 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.24 1.021zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.24 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.56.3z"/>
                                                </svg>
                                            </span>
                                            {language === 'it' ? 'Ascolta su Spotify' : 'Open on Spotify'}
                                        </button>
                                        <button
                                            onClick={() => handleShare(artist)}
                                        >
                                            <span className="material-icons">share</span>
                                            {language === 'it' ? 'Condividi' : 'Share'}
                                        </button>
                                        <button
                                            className="danger"
                                            onClick={() => handleMenuRemove(artist.id)}
                                        >
                                            <span className="material-icons">delete</span>
                                            {language === 'it' ? 'Rimuovi dai preferiti' : 'Remove'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Messaggio se nessun risultato con filtro */}
                {filteredFavorites.length === 0 && selectedGenre && (
                    <div className="favorites-empty-filter">
                        <p>{language === 'it' 
                            ? `Nessun artista con genere "${selectedGenre}"`
                            : `No artists with genre "${selectedGenre}"`}</p>
                    </div>
                )}
            </div>

            {/* Modal conferma rimozione */}
            {showRemoveConfirm && (
                <div className="remove-confirm-overlay" onClick={() => setShowRemoveConfirm(null)}>
                    <div className="remove-confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>{language === 'it' ? 'Rimuovere dai preferiti?' : 'Remove from favorites?'}</h3>
                        <p>{language === 'it' 
                            ? 'Sei sicuro di voler rimuovere questo artista dai tuoi preferiti?'
                            : 'Are you sure you want to remove this artist from your favorites?'}</p>
                        <div className="remove-confirm-buttons">
                            <button
                                className="remove-confirm-cancel"
                                onClick={() => setShowRemoveConfirm(null)}
                            >
                                {language === 'it' ? 'Annulla' : 'Cancel'}
                            </button>
                            <button
                                className="remove-confirm-ok"
                                onClick={() => handleRemoveFavorite(showRemoveConfirm)}
                            >
                                {language === 'it' ? 'Rimuovi' : 'Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Favorites;

